import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';
import { X, Warning } from '@phosphor-icons/react';

// ISO ↔ datetime-local helpers (shared shape with CreateSessionDialog).
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Edit Session dialog — surfaces existing PATCH /event-sessions/{id}.
 *
 * Editable fields:
 *   • name
 *   • starts_at
 *   • registration_closes_at
 *   • allow_self_register
 *   • num_courts
 *   • weeks_planned
 *   • games_per_week
 *
 * Deliberately NOT exposed (per club policy):
 *   • players_per_court — locked to the create-time value
 *   • status — managed via Open/Close registration buttons elsewhere
 *
 * Overflow guard:
 *   When the admin reduces num_courts such that active player registrations
 *   exceed the new active roster capacity, the Save button is disabled until
 *   they tick an explicit "I understand X players will become subs" checkbox.
 *   Backend allows the save unconditionally — the checkbox is purely a UI
 *   guardrail, not a server gate.
 */

// Shared input class for native datetime + number/text inputs. Sized to
// avoid iOS Safari clipping on `type="datetime-local"` and to meet the
// 44px touch-target minimum on mobile.
const INPUT_CLS =
  "w-full px-3 py-2.5 min-h-[44px] rounded-lg border border-[#e3dcd1] bg-white " +
  "text-[15px] appearance-none focus:outline-none focus:border-[#015e3f]/40";

export default function EditSessionDialog({
  open,
  session,
  registrations = [],
  onClose,
  onSaved,
}) {
  const [name, setName] = useState(session?.name || '');
  const [startsAt, setStartsAt] = useState(isoToLocalInput(session?.starts_at));
  const [closesAt, setClosesAt] = useState(isoToLocalInput(session?.registration_closes_at));
  const [allowSelf, setAllowSelf] = useState(!!session?.allow_self_register);
  const [numCourts, setNumCourts] = useState(session?.num_courts ?? 3);
  const [weeksPlanned, setWeeksPlanned] = useState(session?.weeks_planned ?? 8);
  const [gamesPerWeek, setGamesPerWeek] = useState(session?.games_per_week ?? 36);
  const [understandOverflow, setUnderstandOverflow] = useState(false);
  const [saving, setSaving] = useState(false);

  // Active player count = registrations with role='player' and status='active'
  const activePlayers = useMemo(
    () => registrations.filter((r) => r.role === 'player' && (r.status || 'active') === 'active').length,
    [registrations],
  );
  const playersPerCourt = session?.players_per_court ?? 4;
  const newCapacity = Number(numCourts) * playersPerCourt;
  const overflowCount = Math.max(0, activePlayers - newCapacity);
  const overflowRequiresAck = overflowCount > 0;

  if (!open || !session) return null;

  const canSubmit = !saving && (!overflowRequiresAck || understandOverflow);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const patch = {
        name: name.trim(),
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        registration_closes_at: closesAt ? new Date(closesAt).toISOString() : null,
        allow_self_register: allowSelf,
        num_courts: Number(numCourts),
        weeks_planned: Number(weeksPlanned),
        games_per_week: Number(gamesPerWeek),
      };
      const r = await eventAPI.updateSession(session.id, patch);
      toast.success('Session updated');
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-2 sm:p-4"
      data-testid="edit-session-dialog"
    >
      <div className="w-full sm:max-w-xl bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#e3dcd1]">
          <h2 className="font-serif text-2xl text-[#013523]">Edit session</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-session-dialog-close"
            className="text-[#6a766e] hover:text-[#013523] p-1 rounded-lg"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name */}
          <Field label="Session name">
            <input
              data-testid="edit-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>

          {/* Dates — stack until md (768px). At sm (640px+) the modal
              becomes sm:max-w-xl, so a 2-col layout below 768px would still
              give each datetime-local <230px and the native calendar/clock
              widget visually collides on Safari/Chrome. md breakpoint keeps
              each column ≥260px and prevents the overlap the EM saw. */}
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Starts at">
              <input
                data-testid="edit-starts-at-input"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Registration closes">
              <input
                data-testid="edit-closes-at-input"
                type="datetime-local"
                value={closesAt}
                onChange={(e) => setClosesAt(e.target.value)}
                className={INPUT_CLS}
              />
            </Field>
          </div>

          {/* Court-based capacity */}
          <div className="bg-white border border-[#e3dcd1] rounded-xl p-3 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
              Roster
            </div>
            <Field label="Available courts">
              <input
                data-testid="edit-num-courts-input"
                type="number"
                min={1}
                max={50}
                value={numCourts}
                onChange={(e) => setNumCourts(e.target.value)}
                className={`w-32 ${INPUT_CLS.replace('w-full', '').trim()}`}
              />
            </Field>
            <p className="text-xs text-[#6a766e]" data-testid="edit-capacity-summary">
              Active roster capacity: <span className="font-medium text-[#013523]">{newCapacity}</span>{' '}
              ({numCourts} courts × {playersPerCourt} players per court).
              {' '}Overflow players become subs at start time.
            </p>
            {activePlayers > 0 && (
              <p className="text-xs text-[#6a766e]" data-testid="edit-current-regs">
                Currently registered as active players: <span className="font-medium text-[#013523]">{activePlayers}</span>.
              </p>
            )}
          </div>

          {/* Overflow acknowledgement */}
          {overflowRequiresAck && (
            <div
              data-testid="edit-overflow-warning"
              className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2"
            >
              <div className="flex items-start gap-2 text-amber-900">
                <Warning size={18} weight="fill" className="mt-0.5 shrink-0" />
                <div className="text-xs">
                  <div className="font-medium">Overflow at start</div>
                  <div className="mt-1">
                    {activePlayers} players are registered. With {numCourts} courts,{' '}
                    {newCapacity} players will be active and{' '}
                    <span className="font-medium">{overflowCount} will become subs</span>{' '}
                    when this session starts.
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="edit-understand-overflow-checkbox"
                  checked={understandOverflow}
                  onChange={(e) => setUnderstandOverflow(e.target.checked)}
                  className="mt-0.5 accent-[#015e3f]"
                />
                <span className="text-xs text-amber-900">
                  I understand {overflowCount}{' '}
                  {overflowCount === 1 ? 'player' : 'players'} will become subs when this session starts.
                </span>
              </label>
            </div>
          )}

          {/* Other settings */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Weeks planned">
              <input
                data-testid="edit-weeks-planned-input"
                type="number"
                min={1}
                max={52}
                value={weeksPlanned}
                onChange={(e) => setWeeksPlanned(e.target.value)}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Games per week">
              <input
                data-testid="edit-games-per-week-input"
                type="number"
                min={1}
                max={500}
                value={gamesPerWeek}
                onChange={(e) => setGamesPerWeek(e.target.value)}
                className={INPUT_CLS}
              />
            </Field>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="edit-allow-self-register-checkbox"
              checked={allowSelf}
              onChange={(e) => setAllowSelf(e.target.checked)}
              className="mt-0.5 accent-[#015e3f]"
            />
            <div className="text-xs">
              <div className="font-medium text-[#013523]">Allow self-registration</div>
              <div className="text-[#6a766e]">
                When OFF, only admins can add players. When ON, approved members can self-register while registration is open.
              </div>
            </div>
          </label>

          <div className="text-[11px] text-[#6a766e] italic">
            Players-per-court is fixed at {playersPerCourt} for this event type. Contact a club administrator if it needs to change.
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="edit-session-cancel"
              className="flex-1 px-3 py-2 rounded-lg border border-[#e3dcd1] text-sm text-[#013523] hover:bg-[#f0ece3]"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="edit-session-save"
              disabled={!canSubmit}
              className="flex-1 px-3 py-2 rounded-lg bg-[#015e3f] text-white text-sm font-medium hover:bg-[#013523] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">{label}</div>
      {children}
    </label>
  );
}
